import { ref } from 'vue'
import { defineStore } from 'pinia'
import { launchModal, ModalType } from './modalControl'

export enum Theme {
  Dark = 'dark',
  Light = 'light',
  None = 'none'
}

export enum DisplayMode {
  Mobile,
  MediumDesktop,
  LargeDesktop,
  FullDesktop
}

function classifyDisplayMode(width: number): DisplayMode {
  if (width < 600) {
    return DisplayMode.Mobile
  } else if (width < 900) {
    return DisplayMode.MediumDesktop
  } else if (width < 1200) {
    return DisplayMode.LargeDesktop
  } else {
    return DisplayMode.FullDesktop
  }
}

async function getSiteDisplaySettings() {
  const response = await fetch('/api/content/siteDisplaySettings')
  if (response.ok) {
    const data = await response.json()
    return data
  }
  return null
}

/**
 * The router's `kioskAllowed` guard decides which internal pages a kiosk may
 * reach, so the lockdown only has to catch what leaves the site.
 */
export function leavesSite(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute('href')
  // Handler-driven anchors (RouterLink, click handlers) navigate through the router.
  if (href === null) return false
  if (anchor.hasAttribute('download')) return true
  if (anchor.target !== '' && anchor.target !== '_self') return true

  try {
    // Resolved, not pattern-matched: '//host/path' is off-site despite looking
    // relative, and an absolute URL back to this host is not.
    return new URL(href, window.location.href).origin !== window.location.origin
  } catch {
    return true
  }
}

function setFavicon(url: string) {
  var link = document.querySelector("link[rel~='icon']") as any
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = '/api/Content/' + url
}

export const useDisplayConfigStore = defineStore(
  'displayConfig',
  () => {
    const displayMode = ref(classifyDisplayMode(window.innerWidth))
    const displayTheme = ref(Theme.None)
    const startedHeaderLocked = ref(true)
    const headerLocked = ref(false)
    const showAnyNav = ref(true)
    const showFullLogo = ref(determineShowFullLogo())
    const showingMegaMenu = ref(false)
    const bannerMessage = ref<string | null>(null)
    const welcomeMessage = ref<string | null>(null)
    const isKiosk = ref<boolean | null>(null)
    const lockupURL = ref<string | undefined>(undefined)
    const graphicURL = ref<string | undefined>(undefined)
    const squareLogoURL = ref<string | undefined>(undefined)
    const faviconURL = ref<string | undefined>(undefined)
    const showBookstackSearch = ref(false)
    const showLinksManager = ref(false)

    function determineShowFullLogo() {
      return window.innerWidth > 1000 && !headerLocked.value
    }

    async function refresh() {
      if (faviconURL && faviconURL.value != undefined) setFavicon(faviconURL.value)
      displayMode.value = classifyDisplayMode(window.innerWidth)
      showFullLogo.value = determineShowFullLogo()
      await getSiteDisplaySettings().then((message) => {
        // this needs to be first, so that the tab icon is blank for as short a time as possible
        bannerMessage.value = message.bannerMessage
        welcomeMessage.value = message.welcomeMessage
        lockupURL.value = message.lockupURL
        graphicURL.value = message.graphicURL
        squareLogoURL.value = message.squareLogoURL
        faviconURL.value = message.faviconURL
        showBookstackSearch.value = message.showBookstackSearch
        showLinksManager.value = message.showLinksManager
      })
    }

    function toggleMegaMenu() {
      showingMegaMenu.value = !showingMegaMenu.value
    }

    function setTheme(theme: Theme) {
      displayTheme.value = theme
      if (theme === Theme.Dark) {
        document.documentElement.style.setProperty(
          '--image-background',
          "url('/backgrounds/DarkBackground.svg')"
        )
      } else if (theme === Theme.Light) {
        document.documentElement.style.setProperty(
          '--image-background',
          "url('/backgrounds/LightBackground.png')"
        )
      } else {
        document.documentElement.style.setProperty('--image-background', '')
      }
    }

    window.addEventListener('resize', () => {
      showFullLogo.value = determineShowFullLogo()
      displayMode.value = classifyDisplayMode(window.innerWidth)
    })

    window.addEventListener('scroll', () => {
      showFullLogo.value = determineShowFullLogo()
    })

    // Not armed from Kiosk.vue's mount: cards navigate by plain href, so
    // setKioskMode never runs again after the first page load, while isKiosk
    // is restored from storage.
    document.addEventListener('click', blockOffsiteNavigation, true)
    document.addEventListener('auxclick', blockOffsiteNavigation, true)
    setInterval(markOffsiteLinks, 500)

    function blockOffsiteNavigation(e: MouseEvent) {
      if (!isKiosk.value) return

      const anchor = e.target instanceof Element ? e.target.closest('a') : null
      if (!anchor || !leavesSite(anchor)) return

      launchModal({
        message: "Can't navigate away from this page in kiosk mode",
        header: 'Kiosk Mode',
        confirmButtonText: 'OK',
        type: ModalType.Error
      })
      e.stopImmediatePropagation()
      e.preventDefault()
    }

    /** Cursor affordance only; the listener above does the blocking. */
    function markOffsiteLinks() {
      if (!isKiosk.value) return
      document.querySelectorAll('a').forEach((el) => {
        el.classList.toggle('disabled', leavesSite(el))
      })
    }

    function setKioskMode(isKioskMode: boolean) {
      isKiosk.value = isKioskMode
    }

    return {
      displayMode,
      startedHeaderLocked,
      headerLocked,
      showAnyNav,
      showFullLogo,
      refresh,
      showingMegaMenu,
      toggleMegaMenu,
      bannerMessage,
      welcomeMessage,
      displayTheme,
      setTheme,
      isKiosk,
      setKioskMode,
      lockupURL,
      graphicURL,
      squareLogoURL,
      faviconURL,
      showBookstackSearch,
      showLinksManager
    }
  },
  {
    persist: [
      {
        key: 'kiosk-display-config',
        pick: ['isKiosk', 'kioskLocationId']
      },
      {
        key: 'site-display-settings',
        pick: ['faviconURL', 'lockupURL']
      }
    ]
  }
)
